import type { Schemas } from "@softmax/observatory-client/types";
import type { EpisodeRow } from "../types";
import { parseEpisodeSeatResult, wonFlagsOf } from "./episode-results";
import { rankOf } from "./episode-scores";
import {
  mySeatIndex,
  opponentsOf,
  outcomeOf,
  type EpisodesViewer,
} from "./episode-seats";

export function episodeRow({
  entry,
  viewer,
  episodeHrefOf,
  replayHrefOf,
}: {
  readonly entry: Schemas["EpisodeSummary"];
  readonly viewer: EpisodesViewer;
  readonly episodeHrefOf: (episodeId: string) => string;
  readonly replayHrefOf: (episodeId: string) => string;
}): EpisodeRow | null {
  const results = parseEpisodeSeatResult(entry.results);

  if (results === null) return null;

  const seatIndex = mySeatIndex({
    policies: entry.policies,
    names: results.names,
    viewer,
  });

  if (seatIndex === null) return null;

  const { scores } = results;
  const score = scores?.[seatIndex];

  // A missing score cannot establish this seat's rank.
  if (!scores || score === undefined) return null;

  const seats = scores.length;
  const won = wonFlagsOf({ results, seatCount: seats });
  const rank = rankOf({ scores, won, seatIndex, mine: score });
  return {
    episodeId: entry.episode_id,
    createdAt: entry.created_at,
    seats,
    mySeatRank: rank,
    won: won[seatIndex] ?? null,
    topTenPercent: rank <= Math.ceil(seats / 10),
    score,
    scoreDeltaVsMedian: 0,
    maxScore: Math.max(...scores),
    // A replay artifact indicates availability; navigation opens the theater.
    replayHref: entry.replay_url ? replayHrefOf(entry.episode_id) : null,
    episodeHref: episodeHrefOf(entry.episode_id),
    thumbUrl:
      entry.thumbnail_url ??
      `https://softmax-public.s3.amazonaws.com/episode-thumbnails/${encodeURIComponent(entry.episode_id)}.webp`,
    opponents: opponentsOf({
      policies: entry.policies,
      viewer,
      seatIndex,
      won,
      scores,
    }),
    outcome: outcomeOf({ won, seatIndex }),
    turns: results.turns ?? null,
    tournament: entry.tags.source === "tournament",
  };
}
